import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, Sparkles, Sprout, X } from "lucide-react";

import { cn } from "../../../lib/utils";
import useRetryFetch from "../../../hooks/useRetryFetch";
import { useEffectOnce } from "../../../hooks/useEffectOnce";
import {
  addPwfChild,
  createPwf,
  updatePwf,
  type JourneyDraft,
  type Pwf,
} from "../../../api/pwf";
import { personalJourneyDraft } from "../../../api/lifeOs";
import { listTakeaways, editTakeaway } from "../../../api/userTakeaways";
import { pathWeeksRange, trimmedPhaseToIdea } from "../../../lib/draftReview";
import { queryClient } from "../../../lib/queryClient";
import { Button } from "../../ui/button";

// ── Graduation — a life project becomes a journey ─────────────────────────────
// The system already heard the user describe this project in fragments —
// captures, actions, the lens. Graduation drafts the phased journey FROM that
// material (server-side), the user reviews it here, and only then does it
// materialize: the same structures the coach Studio wizard creates, minus the
// template flag — this journey is personal.

type Stage = "drafting" | "review" | "creating" | "error";

// The AI draft must never strand the sheet on the spinner — mirrors
// InterviewWizard's DRAFT_TIMEOUT_MS pattern: after this long we give up and
// drop into the existing error stage (Retry/Skip).
const DRAFT_TIMEOUT_MS = 180_000;

const GraduationSheet: React.FC<{
  project: Pwf;
  onClose: () => void;
  onCreated: (projectId: string) => void;
}> = ({ project, onClose, onCreated }) => {
  const { t } = useTranslation();
  const retryFetch = useRetryFetch();
  const navigate = useNavigate();

  const [stage, setStage] = useState<Stage>("drafting");
  const [draft, setDraft] = useState<JourneyDraft | null>(null);
  const [phasesSel, setPhasesSel] = useState<boolean[]>([]);
  const [creatingLabel, setCreatingLabel] = useState("");
  const [draftLine, setDraftLine] = useState(0);
  // Guards against two overlapping fetchDraft() calls (e.g. a fast
  // Retry double-click, or a slow attempt resolving after a retry already
  // started) racing each other — mirrors InterviewWizard's draftAttemptRef:
  // only the most recent attempt is allowed to setState.
  const draftAttemptRef = useRef(0);
  // Drives the Retry button's disabled state — stage alone can't express
  // "a fetch is in flight" at the button's own call site (it only renders
  // while stage === "error", so comparing stage to "drafting" there is
  // always false and TS correctly rejects it as unreachable).
  const [isFetchingDraft, setIsFetchingDraft] = useState(false);

  const fetchDraft = async () => {
    setStage("drafting");
    setIsFetchingDraft(true);
    const attempt = ++draftAttemptRef.current;
    const iv = setInterval(() => setDraftLine((i) => (i + 1) % 3), 6000);
    try {
      const d = await Promise.race([
        personalJourneyDraft(retryFetch, project._id),
        new Promise<never>((_, reject) =>
          setTimeout(
            () => reject(Object.assign(new Error("draft timeout"), { isTimeout: true })),
            DRAFT_TIMEOUT_MS
          )
        ),
      ]);
      if (attempt !== draftAttemptRef.current) return; // superseded by a newer attempt
      setDraft(d);
      setPhasesSel(d.phases.map(() => true));
      setStage("review");
    } catch {
      if (attempt !== draftAttemptRef.current) return;
      setStage("error");
    } finally {
      clearInterval(iv);
      setIsFetchingDraft(false);
    }
  };

  useEffectOnce(() => {
    fetchDraft();
  });

  // Old-style graduation: just switch on the machinery, shape it by hand.
  const skipDraft = async () => {
    try {
      const studio = ((project.config as any)?.studio ?? {}) as Record<string, any>;
      await updatePwf(retryFetch, project._id, {
        config: { studio: { ...studio, isJourney: true, personal: true } },
      });
      onCreated(project._id);
    } catch (e: any) {
      toast.error(e.message ?? "Could not graduate the project");
    }
  };

  // The shared build: writes the journey root, creates only the kept
  // phases, trims the rest into the Idea Bank (v3 §−2 — never deleted), and
  // archives the legacy pending steps they were drafted from (C-R1 — one
  // live object per task, never two). Returns the journey id — the life
  // project is promoted in place, so that's just project._id.
  const materializeCore = async (): Promise<string> => {
    if (!draft) throw new Error("No draft to materialize");
    const studio = ((project.config as any)?.studio ?? {}) as Record<string, any>;
    const trimmedIdeas = draft.phases
      .filter((_, i) => !phasesSel[i])
      .map(trimmedPhaseToIdea);
    // Same shapes the Studio wizard writes — but personal, never a template.
    await updatePwf(retryFetch, project._id, {
      config: {
        studio: {
          ...studio,
          isJourney: true,
          personal: true,
          summary: draft.summary ?? "",
          do: draft.do ?? "",
          become: draft.become ?? {},
          ideaBank: [...(draft.ideaBank ?? []), ...trimmedIdeas],
        },
      },
    });

    const phases = draft.phases.filter((_, i) => phasesSel[i]);
    for (let i = 0; i < phases.length; i++) {
      const p = phases[i];
      setCreatingLabel(t("grad_adding_phase", { current: i + 1, total: phases.length, title: p.title }));
      const child = await createPwf(retryFetch, {
        title: p.title,
        pwfType: "pwf",
        config: {
          studio: {
            isPhase: true,
            do: p.doSide,
            identity: p.identity,
            traits: p.traits,
            estWeeks: p.estWeeks,
            habits: p.habits,
            checkIn: p.checkIn,
          },
        },
      });
      await addPwfChild(retryFetch, project._id, child._id);
      for (const item of p.doItems ?? []) {
        const doChild = await createPwf(retryFetch, {
          title: item.title,
          pwfType: item.kind,
        });
        await addPwfChild(retryFetch, child._id, doChild._id);
      }
    }

    // C-R1: the drafted phases were built from these — archive (never delete)
    // the legacy pending steps so Today shows one object per task.
    try {
      const pending = await listTakeaways(retryFetch, { type: "action", status: "pending", limit: 200 });
      const legacy = (pending.list ?? []).filter((tk) => tk.pwfMeta?.fiPwf === project._id && !tk.__isPwf);
      for (const tk of legacy) {
        await editTakeaway(retryFetch, { id: tk._id, status: "archived" });
      }
    } catch {
      /* non-fatal — duplication is cosmetic; never block graduation on cleanup */
    }

    // Every exit (Begin, Adjust-in-studio, and any future one) must refresh
    // the cockpit/room caches — this used to live only in each caller's own
    // onCreated, which the studio exit never calls. Doing it here once means
    // callers can't forget it; their own invalidateQueries() becomes
    // redundant-but-harmless.
    queryClient.invalidateQueries();

    return project._id;
  };

  const materialize = async () => {
    if (!draft) return;
    setStage("creating");
    try {
      onCreated(await materializeCore());
    } catch (e: any) {
      toast.error(e.message ?? "Could not create the journey");
      setStage("review");
    }
  };

  // Second ranked exit: same materialization, but hands off to the Studio
  // for hand-editing instead of dropping onto the map.
  const materializeThen = async (dest: "studio") => {
    if (!draft) return;
    setStage("creating");
    try {
      const journeyId = await materializeCore();
      if (dest === "studio") navigate(`/pwf?id=${journeyId}`);
    } catch (e: any) {
      toast.error(e.message ?? "Could not create the journey");
      setStage("review");
    }
  };

  // Recomputed live as the user toggles keep/trim pills below.
  const selectedCount = phasesSel.filter(Boolean).length;
  const selectedPhases = draft ? draft.phases.filter((_, i) => phasesSel[i]) : [];
  const weeksRange = pathWeeksRange(selectedPhases);

  return (
    <div className="fixed inset-0 z-[70] bg-background overflow-y-auto">
      <div className="max-w-2xl mx-auto px-6 py-8 min-h-full flex flex-col">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm font-semibold text-primary flex items-center gap-2">
            <Sprout className="w-4 h-4" />
            {t("grad_title")}
          </p>
          <button
            onClick={onClose}
            className="p-2 rounded-full text-muted-foreground hover:bg-muted"
            aria-label={t("cancel")}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {stage === "drafting" && (
          <div className="flex-1 flex flex-col items-center justify-center text-center py-24">
            <Loader2 className="w-8 h-8 text-primary animate-spin mb-4" />
            <p className="text-sm font-medium text-foreground">
              {t(`grad_drafting_${draftLine}` as any, { title: project.title })}
            </p>
            <p className="text-xs text-muted-foreground mt-1">{t("grad_drafting_hint")}</p>
          </div>
        )}

        {stage === "error" && (
          <div className="flex-1 flex flex-col items-center justify-center text-center py-24 space-y-3">
            <p className="text-sm text-destructive">{t("seed_error")}</p>
            <div className="flex items-center gap-2">
              <Button size="sm" className="rounded-full" disabled={isFetchingDraft} onClick={fetchDraft}>
                {t("ritual_retry")}
              </Button>
              <Button size="sm" variant="ghost" className="rounded-full" onClick={skipDraft}>
                {t("grad_skip")}
              </Button>
            </div>
          </div>
        )}

        {stage === "review" && draft && (
          <div className="space-y-6 pb-12">
            <div>
              <h2 className="text-xl font-semibold text-foreground">{draft.title || project.title}</h2>
              {draft.summary && (
                <p className="text-sm text-muted-foreground mt-1 italic">{draft.summary}</p>
              )}
            </div>

            {/* BECOME — who is on the other side */}
            {(draft.become?.identity || (draft.become?.traits?.length ?? 0) > 0) && (
              <div className="rounded-2xl border border-emerald-200/70 bg-emerald-50/40 p-4">
                <p className="text-[11px] font-semibold uppercase tracking-widest text-emerald-800/70 mb-1.5">
                  {t("grad_become_title")}
                </p>
                {draft.become?.identity && (
                  <p className="text-sm font-semibold text-emerald-900">{draft.become.identity}</p>
                )}
                {(draft.become?.traits ?? []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {draft.become!.traits!.map((tr) => (
                      <span
                        key={tr}
                        className="rounded-full border border-emerald-300/60 bg-white/70 px-2.5 py-1 text-xs text-emerald-900"
                      >
                        {tr}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* The honest arc — how many phases, how many real weeks */}
            <div>
              <p className="text-sm text-foreground">
                {t("grad_path_line", {
                  count: selectedCount,
                  min: weeksRange.min,
                  max: weeksRange.max,
                })}
              </p>
              <p className="text-xs text-muted-foreground">{t("grad_trim_hint")}</p>
            </div>

            {/* Phases */}
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground/70 mb-2">
                {t("grad_phases_title", { count: draft.phases.length })}
              </p>
              <div className="space-y-2">
                {draft.phases.map((p, i) => {
                  const selected = phasesSel[i];
                  return (
                    <div
                      key={i}
                      className={cn(
                        "rounded-xl border bg-card p-3.5 transition-opacity",
                        selected ? "border-primary/20" : "border-gray-200 opacity-60"
                      )}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={cn(
                            "mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-bold",
                            selected
                              ? "bg-primary text-primary-foreground"
                              : "border border-border text-muted-foreground"
                          )}
                        >
                          {i + 1}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <p
                              className={cn(
                                "text-sm font-medium text-foreground",
                                !selected && "text-muted-foreground line-through"
                              )}
                            >
                              {p.title}
                            </p>
                            <button
                              type="button"
                              aria-pressed={selected}
                              onClick={() =>
                                setPhasesSel((sel) => sel.map((x, j) => (j === i ? !x : x)))
                              }
                              className={cn(
                                "flex min-h-[44px] min-w-[44px] flex-shrink-0 items-center justify-center rounded-full px-3 text-xs font-semibold transition-colors",
                                selected
                                  ? "bg-primary/10 text-primary"
                                  : "bg-amber-100 text-amber-800"
                              )}
                            >
                              {selected ? t("grad_keep") : t("grad_trimmed")}
                            </button>
                          </div>
                          <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
                            {p.identity && (
                              <span className="text-emerald-700">
                                {t("grad_becoming")} {p.identity}
                              </span>
                            )}
                            {typeof p.estWeeks === "number" && (
                              <span className="rounded-full bg-muted px-1.5 py-0.5 text-[11px]">
                                {t("grad_est_weeks", { count: p.estWeeks })}
                              </span>
                            )}
                            {(p.doItems ?? []).length > 0 && (
                              <span>{t("grad_item_count", { count: p.doItems!.length })}</span>
                            )}
                          </p>
                          {(p.doItems ?? []).length > 0 && (
                            <ul className="mt-1.5 space-y-0.5">
                              {p.doItems!.map((d, j) => (
                                <li key={j} className="text-xs text-muted-foreground flex items-center gap-1.5">
                                  <span className="w-1 h-1 rounded-full bg-muted-foreground/40 flex-shrink-0" />
                                  {d.title}
                                </li>
                              ))}
                            </ul>
                          )}
                          {(p.habits ?? []).length > 0 && (
                            <p className="text-[11px] text-primary/70 mt-1.5 flex items-center gap-1">
                              <Sparkles className="w-3 h-3" />
                              {t("grad_phase_habits", { count: p.habits.length })}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Identity ladder */}
            <div className="rounded-2xl border border-border/60 bg-card p-4">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground/70 mb-1.5">
                {t("grad_ladder_title")}
              </p>
              <p className="text-[13px] text-foreground/85 leading-relaxed">
                {selectedPhases.map((p) => p.identity).filter(Boolean).join(" → ")}
              </p>
              <p className="text-[11.5px] text-muted-foreground mt-1.5">{t("grad_ladder_note")}</p>
            </div>

            {/* Three ranked exits */}
            <div className="flex items-center justify-between gap-4 pt-2 border-t border-border/60">
              <div className="flex min-w-0 flex-col gap-1">
                <p className="text-xs text-muted-foreground">{t("grad_drafting_hint")}</p>
                <button
                  onClick={skipDraft}
                  className="text-left text-xs text-muted-foreground/70 hover:text-foreground underline-offset-2 hover:underline"
                >
                  {t("grad_skip_empty")}
                </button>
              </div>
              <div className="flex flex-shrink-0 items-center gap-2">
                <Button
                  variant="outline"
                  className="rounded-full"
                  onClick={() => materializeThen("studio")}
                >
                  {t("grad_adjust_studio")}
                </Button>
                <Button
                  className="rounded-full"
                  disabled={!phasesSel.some(Boolean)}
                  onClick={materialize}
                >
                  {t("grad_begin", { count: selectedCount })}
                </Button>
              </div>
            </div>
          </div>
        )}

        {stage === "creating" && (
          <div className="flex-1 flex flex-col items-center justify-center text-center py-24">
            <Loader2 className="w-8 h-8 text-primary animate-spin mb-4" />
            <p className="text-sm text-muted-foreground">{creatingLabel || t("grad_creating")}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GraduationSheet;
